import * as React from 'react';
import { useToast } from '@teamsnap/snap-ui'
import {
  PaymentPlansState,
  LoadingType,
  PaymentPlanFormValues,
  defaultPlan,
} from './types';
import { InstallmentPlan } from '../types';
import api from 'core/api';
import {
  getFormValuesFromPlan,
  getPlanFromFormValues,
  requiredFieldsMissing,
  validateFormField,
  datePresets,
} from './helpers';

export const paymentPlansService = {
  getPaymentPlans: async (scopeId: number, scopeType: string) => {
    const response = await api.get<InstallmentPlan[]>(
      `/installment_plans?scope_type=${scopeType}&scope_id=${scopeId}`
    );
    return response.data;
  },
  createPlan: async (
    plan: Partial<InstallmentPlan>
  ): Promise<InstallmentPlan> => {
    const response = await api.post<InstallmentPlan>(
      '/installment_plans',
      plan
    );
    return response.data;
  },
  updatePlan: async (plan: Partial<InstallmentPlan>, id: number) => {
    const response = await api.put<InstallmentPlan>(
      `/installment_plans/${id}`,
      plan
    );
    return response.data;
  },
};

export const usePaymentPlans = ({
  scopeId,
  scopeType,
}: {
  scopeId: number;
  scopeType: string;
}) => {
  const [plans, setPlans] = React.useState<InstallmentPlan[]>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [formValues, setFormValues] =
    React.useState<PaymentPlanFormValues | null>(null);
  const [originalformValues, setOriginalFormValues] =
    React.useState<PaymentPlanFormValues | null>(null); // to store original values
  const [loading, setLoading] = React.useState<PaymentPlansState['loading']>({
    [LoadingType.GetPlans]: false,
    [LoadingType.CreateOrUpdatePlan]: false,
  });
  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  const { toast, dismiss } = useToast();

  React.useEffect(() => {
    const getPlans = async () => {
      setLoading({ ...loading, [LoadingType.GetPlans]: true });
      try {
        const plans = await paymentPlansService.getPaymentPlans(
          scopeId,
          scopeType
        );
        setPlans(plans);
      } catch (error) {
        setLoading({
          ...loading,
          [LoadingType.GetPlans]: false,
          error: 'error.message',
        });
        toast({
          title: 'Failed to fetch payment plans',
          variant: 'negative',
        });
      } finally {
        setLoading({ ...loading, [LoadingType.GetPlans]: false });
      }
    };
    getPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, scopeType]);

  React.useEffect(() => () =>  {
      // cleanup function to dismiss any open toasts
      dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPlan = () => {
    const formValues = getFormValuesFromPlan(defaultPlan);
    // initialize the form with default date
    formValues.scheduledDates = [datePresets.getOneMonthFromNow()];
    setFormValues(formValues);
    setIsOpen(true);
  };

  const editPlan = (planId: number) => {
    const formValues = getFormValuesFromPlan(
      plans.find((plan) => plan.id === planId)
    );
    setFormValues(formValues);
    setOriginalFormValues(formValues);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setFormValues(null);
    setErrors({});
    setIsOpen(false);
  };

  const updateField = (field: string, value: any) => {
    if (!formValues) return;
    const validationErrors = validateFormField(
      formValues,
      errors,
      field,
      value,
      originalformValues,
    );
    setErrors(validationErrors);
    setFormValues({ ...formValues, [field]: value });
  };

  const createOrUpdatePlan = async (
    plan: Partial<InstallmentPlan>,
    id?: number
  ) => {
    setLoading({ ...loading, [LoadingType.CreateOrUpdatePlan]: true, id });
    try {
      const updatedPlan = { ...plan };
      if (id) {
        const plan = plans.find((p) => p.id === id);
        const savedPlan = await paymentPlansService.updatePlan(
          { ...plan, ...updatedPlan },
          id
        );
        setPlans(plans.map((p) => (p.id === savedPlan.id ? savedPlan : p)));
        toast({
          title: 'Payment plan updated',
          variant: 'success',
        });
        return;
      }
      const addtlFields = { enabled: true, scopeId, scopeType };
      const createdPlan = await paymentPlansService.createPlan({
        ...updatedPlan,
        ...addtlFields,
      });
      setPlans([...plans, createdPlan]);
      toast({
          title: 'Payment plan created',
          variant: 'success',
        });
    } catch (error) {
      setLoading({
        ...loading,
        [LoadingType.CreateOrUpdatePlan]: false,
        error: 'error.message',
      });
      toast({
          title: 'Failed to save plan',
          variant: 'negative',
        });
    } finally {
      setLoading({ ...loading, [LoadingType.CreateOrUpdatePlan]: false, id: undefined });
    }
  };

  const togglePlan = async (planId: number, enabled: boolean) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    const updatedPlan = { ...plan, enabled };
    await createOrUpdatePlan(updatedPlan, planId);
  };

  const savePlan = () => {
    if (!formValues) return;
    const updatedPlan = getPlanFromFormValues(formValues);
    createOrUpdatePlan(updatedPlan, formValues.id);
    closeDrawer();
  };

  const isSaveDisabled = React.useMemo(() => {
    if (!formValues) return true;
    const hasErrors = Object.values(errors).some(
      (error) => error.filter(Boolean).length > 0
    );
    if (hasErrors) return true;
    // no fields edited because the error property would be empty array when touched
    const isUntouched =  Object.keys(errors).length === 0;
    if (formValues?.id && isUntouched) return true; // dont allow save if no changes made
    if (requiredFieldsMissing(formValues)) return true;
  }, [formValues, errors]);

  return {
    loading,
    plans,
    isOpen,
    formValues,
    errors,
    addPlan,
    editPlan,
    closeDrawer,
    togglePlan,
    savePlan,
    updateField,
    isSaveDisabled,
  };
};
